import { useRef, useMemo } from 'react';
import { CommandStack, TokenRegistry, type TokenManifest } from '@bq/designer-core';
import { DesignerProvider, DesignerSurface } from '@bq/designer-components';
import { Heading, Text, Button, Badge } from '@bq/web-components';
import manifest from '@bq/web-tokens/dist/tokens-manifest.json';

function PreviewContent() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
      {/* Typography card */}
      <div
        style={{
          backgroundColor: 'rgba(var(--bq-n000), 1)',
          borderRadius: 'var(--bq-br3)',
          padding: 'var(--bq-size2)',
          boxShadow: 'var(--bq-shadow2)',
        }}
      >
        <Heading level={2} as={3}>
          Design Token Preview
        </Heading>
        <Text size="r1" color="n700">
          This card responds to token overrides in real time. Adjust colors,
          spacing, typography, and border-radius tokens in the inspector panel
          to see changes reflected here instantly.
        </Text>
        <Text size="r3" color="n500">
          All visual properties are driven by CRD R1 design tokens.
        </Text>
      </div>

      {/* Buttons row */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--bq-size1)',
          alignItems: 'center',
        }}
      >
        <Button variant="primary">Primary Action</Button>
        <Button variant="secondary">Secondary</Button>
        <Button variant="secondaryLight" size="small">
          Small Button
        </Button>
      </div>

      {/* Badges row */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--bq-size0-5)',
          alignItems: 'center',
        }}
      >
        <Badge>Default</Badge>
        <Badge variant="success">Success</Badge>
        <Badge variant="warning">Warning</Badge>
        <Badge variant="danger">Danger</Badge>
      </div>

      {/* Color swatch grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
          gap: 'var(--bq-size0-5)',
        }}
      >
        {['p500', 'p400', 'p300', 's500', 's400', 's300'].map((token) => (
          <div
            key={token}
            style={{
              backgroundColor: `rgba(var(--bq-${token}), 1)`,
              borderRadius: 'var(--bq-br2)',
              height: '48px',
              display: 'flex',
              alignItems: 'flex-end',
              padding: '4px 6px',
            }}
          >
            <span
              style={{
                fontSize: '10px',
                color: 'rgba(255,255,255,0.85)',
                fontFamily: 'var(--bq-ff-mono)',
              }}
            >
              {token}
            </span>
          </div>
        ))}
      </div>

      {/* Spacing demo */}
      <div
        style={{
          backgroundColor: 'rgba(var(--bq-n100), 1)',
          borderRadius: 'var(--bq-br2)',
          padding: 'var(--bq-size1)',
        }}
      >
        <Heading level={4}>Spacing Demo</Heading>
        <div
          style={{
            display: 'flex',
            gap: 'var(--bq-size1)',
            marginTop: 'var(--bq-size0-75)',
          }}
        >
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              style={{
                flex: 1,
                backgroundColor: 'rgba(var(--bq-p500), 0.15)',
                borderRadius: 'var(--bq-br2)',
                padding: 'var(--bq-size1)',
                textAlign: 'center',
              }}
            >
              <Text size="r3" color="p500">
                Block {i}
              </Text>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function DesignerPage() {
  const stackRef = useRef(new CommandStack());
  const registry = useMemo(
    () => new TokenRegistry(manifest as unknown as TokenManifest),
    [],
  );

  return (
    <DesignerProvider commandStack={stackRef.current} registry={registry}>
      <DesignerSurface>
        <PreviewContent />
      </DesignerSurface>
    </DesignerProvider>
  );
}
